import React, { useState, useEffect } from 'react';
import styled from '@emotion/styled';
import { useTheme } from '@emotion/react';
import { useSession } from 'next-auth/react';
import { Box, Snackbar } from '@mui/material';
import MuiAlert from '@mui/material/Alert';
import { logServerException } from 'appinsights/clientAppInsights';
import SubscriberModuleCard from 'components/common/SubscriberModuleCard';
import CardGrid from 'components/common/subcomponents/CardGrid';
import { hasModuleAccess } from '../../services/subscriptionUtils';
import PleaseWait from '../common/PleaseWait';
import { logClientException } from '../../appinsights/clientAppInsights';
import useMyLearning from '../../hooks/useMyLearning';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import { KitCard } from '@boystownorg/bi-cms-component-lib';
import { extraComponentStyles } from './styles';
import hex2rgba from 'services/colorUtils/hex2rgba';
import { buttonColor } from 'services/styleUtils/buttonStyleUtils';

const defaultErrorMessage =
  'An unexpected error has occured. Please try again.';

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

const SubscriberLandingHeading = (props) => {
  const { customData, module, page } = props;

  const theme = useTheme();
  const isMyLearning = useMyLearning();

  const [canAccessModules, setCanAccessModules] = useState(false);
  const [modules, setModules] = useState();
  const [modulesSet, setModulesSet] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  const [showSeries, setShowSeries] = useState(true);
  const [subscriptionNew, setSubscriptionNew] = useState(false);
  const [subscriptionUpdated, setSubscriptionUpdated] = useState(false);

  const { data: session, status } = useSession();

  useEffect(() => {
    let moduleArray = [];
    customData.mods &&
      customData.mods.length > 0 &&
      customData.mods.forEach((mod, i) => {
        moduleArray.push(mod);
      });
    setModules(moduleArray);
    setModulesSet(true);

    setTimeout(() => {
      badgeProduct(moduleArray);
    }, 2000);
  }, [module, customData]);

  useEffect(() => {
    setCanAccessModules(hasModuleAccess(session, 'wms'));
  }, [session]);

  useEffect(() => {
    if (modulesSet && canAccessModules) {
      fetchUserRegistrations();
    }
  }, [session?.user?.email, modulesSet, canAccessModules]);

  const getFetchOptions = (method, body) => {
    const options = {
      method: method,
      headers: {
        'Content-Type': 'application/json',
      },
    };
    if (body) {
      options.body = JSON.stringify(body);
    }
    return options;
  };

  const fetchUserRegistrations = async (isOnClose = false) => {
    try {
      if (!session?.user?.email) {
        return;
      }
      setIsLoading(true);

      // get tags for all modules, since we need to check completion on all submodules
      const allSubMods = [];
      modules.forEach((mod) => {
        mod?.subMods?.forEach((sub) => {
          allSubMods.push(sub);
        });
      });
      const allTags = [];
      allSubMods.forEach((sub) => {
        sub.fields?.parentModuleTags?.split(',').forEach((tag) => {
          if (tag) {
            allTags.push(tag);
          }
        });
      });

      const body = {
        email: session?.user?.email,
        tags: allTags,
      };

      // fetch all registrations for the user
      let regs = [];
      const res = await fetch(
        '/api/scorm/fetch-registrations',
        getFetchOptions('POST', body),
      );
      if (res && res.ok && res.status === 200) {
        const bodyJson = await res.json();
        regs = bodyJson.registrations;

        let more = bodyJson.more;

        while (more && more.length > 0) {
          const newBody = {
            email: session?.user?.email,
            more: more,
          };
          const resmore = await fetch(
            '/api/scorm/fetch-registrations',
            getFetchOptions('POST', newBody),
          );
          if (resmore.ok && resmore.status === 200) {
            const morebodyJson = await resmore.json();
            const moreregs = morebodyJson.registrations;

            moreregs.forEach((morereg, idx) => {
              regs.push(morereg);
            });

            const moreloop = morebodyJson.more;
            more = moreloop;
          }
        }
      }

      // now we have the user's registrations.
      // loop through registrations and mark sub completion status
      regs
        .sort((a, b) => {
          if (a.id > b.id) return 1;
          if (a.id < b.id) return -1;
          return 0;
        })
        .forEach((reg) => {
          const sub = allSubMods.find(
            (s) => s.fields?.scormCourse?.id === reg.course.id,
          );
          // Handle multiple records for single sub-module:
          // Only update if the instance is > than what is already there (this handles updates to courses)
          if (sub && (sub.instance < reg.instance || !sub.instance)) {
            sub.instance = reg.instance;
            sub.registrationCompletion = reg.registrationCompletion;
            sub.registrationSuccess = reg.registrationSuccess;
            sub.registeredVersion = reg.course.version;
          }
        });

      modules?.forEach((mod) => {
        let allComplete = true;
        let allNotStarted = true;

        mod?.subMods?.forEach((sub) => {
          if (sub.fields.hasNewVersion === 'true') {
            const versionNum = parseInt(sub.fields.newVersionNumber, 10);
            if (!isNaN(versionNum)) {
              if (sub.registeredVersion < versionNum) {
                sub.hasNewVersion = true;
                mod.hasNewVersion = true;
              }
            }
          }
          if (sub.fields.isNew === 'true') {
            sub.isNew = true;
            mod.isNew = true;
          }
          // Checking registrationCompletion values for all COMPLETED or all UNDEFINED (Not Started)
          switch (sub.registrationCompletion) {
            case 'COMPLETED':
              if (
                sub.registrationSuccess === 'UNKNOWN' ||
                sub.registrationSuccess === 'PASSED'
              ) {
                // passed
              } else {
                allComplete = false;
              }
              allNotStarted = false;
              break;
            case 'INCOMPLETE':
              allComplete = false;
              allNotStarted = false;
              break;
            default:
              allComplete = false;
              break;
          }
        });
        // Evaluate the vars to determine state
        if (allComplete) {
          mod.state = 'Completed';
        } else if (allNotStarted) {
          mod.state = 'Start';
        } else {
          mod.state = 'Continue';
        }
      });

      setIsLoading(false);
    } catch (error) {
      setErrorMessage(defaultErrorMessage);
      setIsLoading(false);
      console.log(error);
      logClientException(error);
    }
  };

  const handleAlertClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setErrorMessage(null);
  };

  const calculateCompletionPercent = (modules) => {
    if (!modules || modules.length === 0) return 0;
    let numMods = 0;
    let numSubMods = 0;
    let numSubModsCompleted = 0;
    modules.forEach((m) => {
      numMods = numMods + 1;
      m.subMods.forEach((s) => {
        if (s.fields.includeInCompletionStatus === 'true') {
          numSubMods = numSubMods + 1;
          if (s.registrationCompletion === 'COMPLETED') {
            numSubModsCompleted = numSubModsCompleted + 1;
          }
        }
      });
    });
    if (numSubMods === numSubModsCompleted) {
      return 100;
    } else {
      // console.log(
      //   'Completed: ',
      //   numSubModsCompleted,
      //   ' Total: ',
      //   numSubMods,
      //   ' Percent: ',
      //   (numSubModsCompleted / numSubMods) * 100 + '%',
      // );
      return (numSubModsCompleted / numSubMods) * 100;
    }
  };

  const badgeProduct = (mods) => {
    mods.forEach((mod) => {
      mod.subMods?.forEach((sub) => {
        if (sub.fields.isNew === 'true') {
          if (!subscriptionNew) setSubscriptionNew(true);
          sub.isNew = true;
          mod.isNew = true;
        } else if (sub.fields.hasNewVersion === 'true') {
          const versionNum = parseInt(sub.fields.newVersionNumber, 10);
          if (!isNaN(versionNum)) {
            if (sub.registeredVersion < versionNum) {
              if (!subscriptionUpdated) setSubscriptionUpdated(true);
              sub.hasNewVersion = true;
              mod.hasNewVersion = true;
            }
          }
        }
      });
    });
  };

  const MainFrame = styled.div`
    display; flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    padding: 25px auto 25px auto;
    width: 100%;
    @media screen and (min-width: 992px) {
      padding: 25px auto 25px auto;
    }
    @media screen and (max-width: 992px) {
      padding: 5px auto 5px auto;
    }
  `;

  const HeadingCardFrame = styled.div`
    background-color: transparent;
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: 0 2em;
    @media screen and (min-width: 992px) {
      margin: 40px 150px;
    }
    @media screen and (max-width: 992px) {
      margin: 10px 20px;
    }
  `;

  const Container = styled.div`
    width: 100%;
    margin-top: 30px;
    padding-bottom: 50px;
  `;

  const TitleContainer = styled.div`
    width: 100%;
    ${module.fields.hideTitleWhenSubscriberExists === 'true'
      ? 'display: none;'
      : 'display: flex;'}
    flex-direction: column;
    justify-content: center;
    align-items: flex-start;
    margin-left: -20px;
  `;

  const Title = styled.div`
    margin: 25px 20px 0 20px;
    font-weight: 700;
    display: flex;
    justify-content: center;
    align-items: center;
    text-transform: uppercase;
    @media screen and (min-width: 992px) {
      font-size: 1.75rem;
      line-height: 2.255rem;
    }
    @media screen and (max-width: 992px) {
      font-size: 1.325rem;
      line-height: 1.575rem;
    }
  `;

  const ProductName = styled.div`
    font-weight: 600;
    display: flex;
    justify-content: center;
    align-items: center;
    padding-bottom: 10px;
    color: ${theme.palette.common.black};
    @media screen and (min-width: 992px) {
      font-size: 1.25rem;
      line-height: 1.313rem;
    }
    @media screen and (max-width: 992px) {
      font-size: 1.425rem;
      line-height: 1.55rem;
    }
  `;

  const ShowSeriesLinkContainer = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
  `;

  const ShowSeriesLink = styled.div`
    margin: 15px 0 -15px 0;
    font-weight: 600;
    cursor: pointer;
    @media screen and (min-width: 992px) {
      font-size: 1.1rem;
      line-height: 1.225rem;
    }
    @media screen and (max-width: 992px) {
      font-size: 1rem;
      line-height: 1.125rem;
    }
  `;

  const IntroductionBox = styled.div`
    background-color: ${theme.palette.secondaryLightBlue50.main};
    margin: 0;
  `;

  const IntroContainer = styled.div`
    @media screen and (min-width: 992px) {
      width: 100%;
      padding: 5px 15px 0 15px;
      display: flex;
      flex-direction: row;
      justify-content: space-between;
      align-items: center;
    }
    @media screen and (max-width: 992px) {
      width: 100%;
      padding: 5px 5px 10px 5px;
      display: flex;
      flex-direction: row;
      justify-content: center;
    }
  `;

  const HeadingTextContainer = styled.div`
    @media screen and (min-width: 992px) {
      width: 90%;
      padding: 20px;
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: flex-start;
    }
    @media screen and (max-width: 992px) {
      display: none;
    }
  `;

  const HeadingTextMobileContainer = styled.div`
    @media screen and (min-width: 992px) {
      display: none;
    }
    @media screen and (max-width: 992px) {
      width: 100%;
      padding: 10px;
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: flex-start;
    }
  `;

  const CompletionStat = styled.div`
    background-color: rgba(0, 136, 204, 1);
    padding: 15px;
    display: flex;
    @media screen and (min-width: 992px) {
      width: 155px;
      margin: 0 20px 15px 20px;
      border-radius: 6px;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      text-align: center;
    }
    @media screen and (max-width: 992px) {
      width: 75%;
      margin-bottom: 15px;
      border-radius: 0 6px 6px 0;
      height: 80px;
      flex-direction: row;
      justify-content: flex-start;
      align-items: center;
      margin-left: 5px;
    }
  `;

  const Complete100Banner = styled.div`
    background-color: transparent;
    width: 100%;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    @media screen and (min-width: 992px) {
      margin: 0 20px 15px 20px;
    }
    @media screen and (max-width: 992px) {
      height: 80px;
      width: 80px;
      margin: 0 90px 0 90px;
    }
  `;

  const StatText = styled.div`
    color: ${theme.palette.common.white};
    @media screen and (min-width: 992px) {
      font-size: 1.125rem;
      line-height: 1.313rem;
      font-weight: 600;
    }
    @media screen and (max-width: 992px) {
      font-size: 1.125rem;
      line-height: 1.313rem;
      font-weight: 600;
      margin: 0 15px 0 20px;
    }
  `;

  const StatComplete = styled.div`
    color: ${theme.palette.common.white};
    @media screen and (min-width: 992px) {
      font-size: 3rem;
      line-height: 3.45rem;
      font-weight: 700;
    }
    @media screen and (max-width: 992px) {
      font-size: 3rem;
      line-height: 3.45rem;
      font-weight: 700;
      margin-right: 20px;
    }
  `;

  const SeriesStatus = styled.div`
    color: ${theme.palette.common.white};
    display: flex;
    cursor: pointer;
    @media screen and (min-width: 992px) {
      margin-top: 10px;
      flex-direction: row;
      justify-content: flex-start;
      align-items: center;
    }
    @media screen and (max-width: 992px) {
      margin: 15px 0 15px 0;
      flex-direction: row;
      justify-content: center;
      align-items: center;
    }
  `;

  const BadgeBanner = styled.div`
    color: #fff;
    background: linear-gradient(
      45deg,
      ${theme.palette.info.light} 40%,
      #fafafa 50%,
      ${theme.palette.info.light} 60%
    );
    background-size: 300%;
    background-position-x: 100%;
    animation: shimmer 30s infinite linear;
    margin-top: -10px;
    background-color: ${theme.palette.info.light};
    padding: 0 25px;
    border-radius: 5px;
    position: relative;
    &:after {
      content: '';
      position: absolute;
      top: 2px;
      right: 0.8px;
      width: 0;
      height: 0;
      border-left: 16px solid transparent;
      border-top: 16px solid rgba(238, 245, 248, 1);
      clip-path: polygon(100% 0, 0 100%, 100% 100%);
      margin-right: -1px;
    }
  `;

  const MobileBadgeBannerContainer = styled.div`
    margin: 10px 0 10px 0;
    display: flex;
    justify-content: center;
    align-items: center;
  `;

  return (
    <MainFrame>
      <HeadingCardFrame>
        <TitleContainer>
          <Title style={{ color: module.fields.titleColor }}>
            {module.fields.title}
          </Title>
        </TitleContainer>
        <KitCard style={{ width: '100%', paddingBottom: '30px' }}>
          <IntroductionBox>
            <IntroContainer>
              <ImageContainer
                style={{
                  backgroundImage: `url(${module?.fields?.headingImage?.url})`,
                }}
              ></ImageContainer>
              <HeadingTextContainer>
                <ProductName>{module.fields.product}</ProductName>
                <IntroText>{module.fields.description}</IntroText>
                <SeriesStatus onClick={(e) => setShowSeries(!showSeries)}>
                  {(subscriptionNew || subscriptionUpdated) && (
                    <>
                      <style>{extraComponentStyles(theme)}</style>
                      <BadgeBanner className="shimmer">NEW</BadgeBanner>
                    </>
                  )}
                  <span
                    style={{
                      color: '#000',
                      marginLeft: '15px',
                      marginTop: '-10px',
                    }}
                  >
                    {subscriptionNew
                      ? 'Module published'
                      : subscriptionUpdated
                        ? 'Module updated'
                        : ''}
                  </span>
                </SeriesStatus>
              </HeadingTextContainer>
              {calculateCompletionPercent(modules) === 100 ? (
                <Complete100Banner>
                  <img
                    src="https://content.liftwithboystown.org/images/100complete.svg"
                    alt="Percent Complete: 100%"
                  />
                </Complete100Banner>
              ) : (
                <CompletionStat>
                  <StatText>Percent Completed</StatText>
                  <StatComplete>
                    {calculateCompletionPercent(modules).toFixed(0)}%
                  </StatComplete>
                </CompletionStat>
              )}
            </IntroContainer>
            <HeadingTextMobileContainer>
              <ProductName>{module.fields.product}</ProductName>
              <IntroText>{module.fields.description}</IntroText>
              <MobileBadgeBannerContainer>
                {(subscriptionNew || subscriptionUpdated) && (
                  <>
                    <style>{extraComponentStyles(theme)}</style>
                    <BadgeBanner className="shimmer">NEW</BadgeBanner>
                  </>
                )}
                <span
                  style={{
                    color: '#000',
                    marginLeft: '15px',
                    marginTop: '-10px',
                  }}
                >
                  {subscriptionNew
                    ? 'Module published'
                    : subscriptionUpdated
                      ? 'Module updated'
                      : ''}
                </span>
              </MobileBadgeBannerContainer>
            </HeadingTextMobileContainer>
          </IntroductionBox>
          <ShowSeriesLinkContainer>
            <ShowSeriesLink onClick={(e) => setShowSeries(!showSeries)}>
              {showSeries ? (
                <span>{module.fields.hideSeriesText}</span>
              ) : (
                <span>{module.fields.showSeriesText}</span>
              )}{' '}
              {showSeries ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />}
            </ShowSeriesLink>
          </ShowSeriesLinkContainer>
          {/* {isMyLearning && session && canAccessModules && showSeries && ( */}
          {session && canAccessModules && showSeries && (
            <Container>
              <PleaseWait isLoading={isLoading} />
              <Snackbar
                open={errorMessage}
                autoHideDuration={6000}
                onClose={handleAlertClose}
              >
                <Alert
                  onClose={handleAlertClose}
                  severity="error"
                  sx={{ width: '100%' }}
                >
                  {errorMessage}
                </Alert>
              </Snackbar>

              <Box
                sx={{
                  width: '100%',
                  ml: 'auto',
                  mr: 'auto',
                  maxWidth: {
                    sm: '540px',
                    md: '768px',
                    lg: '1200px',
                    xl: '1200px',
                  },
                }}
              >
                <CardGrid style={{ margin: '-10px 10px -50px 10px' }}>
                  {modules.map((m) => (
                    <SubscriberModuleCard
                      key={m.contentID}
                      module={m}
                      buttonColor={module.fields.buttonColor}
                      buttonTextColor={module.fields.buttonTextColor}
                      cardHeaderColor={module.fields.cardHeaderColor}
                      cardHeaderTabColor={module.fields.cardHeaderTabColor}
                      cardHeaderTextColor={module.fields.cardHeaderTextColor}
                      hasAccess={canAccessModules}
                      canAccessModules={canAccessModules}
                    />
                  ))}
                </CardGrid>
              </Box>
            </Container>
          )}
        </KitCard>
      </HeadingCardFrame>
    </MainFrame>
  );
};

const resolveSeriesUrls = function (sitemap, posts) {
  let dynamicUrls = {};
  posts.forEach((post) => {
    Object.keys(sitemap).forEach((path) => {
      if (sitemap[path].contentID === post.contentID) {
        if (path.indexOf('well-managed-schools') > 0) {
          dynamicUrls[post.contentID] = path;
        }
      }
    });
  });
  return dynamicUrls;
};

// This is called during server-side rendering (not part of NextJS).
SubscriberLandingHeading.getCustomInitialProps = async ({
  agility,
  channelName,
  languageCode,
}) => {
  const api = agility;

  let modArray = [];
  try {
    //get sitemap in order to get the dynamic urls
    let sitemap = await api.getSitemapFlat({
      channelName: channelName,
      languageCode,
    });

    // get modules
    let raw = await api.getContentList({
      referenceName: 'ntrwmsmodulelist',
      languageCode,
      contentLinkDepth: 2,
      // expandAllContentLinks: true,
      depth: 2,
      sort: 'fields.sortOrder',
      take: 50,
    });

    // get our sub modules
    // fetch all (limit of 250 per fetch)
    let subMods = await api.getContentList({
      referenceName: 'ntrwmssubmodulelist',
      languageCode,
      sort: 'fields.subModuleName',
      depth: 3,
      take: 250,
    });

    const dynamicUrls = resolveSeriesUrls(sitemap, raw.items);

    let mods = raw.items.map((mod) => {
      const url = dynamicUrls[mod.contentID];
      if (url == undefined) {
        console.log(
          'Failed to get url for ' +
            mod.contentID +
            '  ' +
            mod.fields.moduleName,
        );
      }
      let subs = subMods.items
        .filter((s) => s.fields.parentModule.contentID === mod.contentID)
        .map((s) => {
          try {
            if (s.fields.scormCourse) {
              s.fields.scormCourse = JSON.parse(s.fields.scormCourse);
            }
          } catch (error) {
            console.log(error);
          }
          return s;
        });
      mod.url = url;
      mod.subMods = subs ? subs : null;
      return mod;
    });

    return {
      mods,
    };
  } catch (error) {
    await logServerException(error);
    if (console) console.error(error);
  }
};

const ImageContainer = styled('div')(({ theme }) => ({
  [theme.breakpoints.up('md')]: {
    width: '165px',
    height: '160px',
    backgroundSize: '150%',
    backgroundRepeat: 'no-repeat',
    borderRadius: '6px',
  },
  [theme.breakpoints.down('md')]: {
    width: '80px',
    height: '80px',
    backgroundSize: '135%',
    backgroundRepeat: 'no-repeat',
    borderRadius: '6px 0 0 6px',
  },
}));

const IntroText = styled('div')(({ theme }) => ({
  color: theme.palette.common.black,
  fontSize: '1.063rem',
  lineHeight: '1.75rem',
  '& li': {
    '&::before': {
      backgroundColor: '#000 !important',
    },
  },
  [theme.breakpoints.up('md')]: {
    marginBottom: '10px',
  },
  [theme.breakpoints.down('md')]: {
    marginBottom: '25px',
  },
}));

export default SubscriberLandingHeading;
